const TIMEZONE_FALLBACK = [
  'UTC',
  'Africa/Johannesburg',
  'America/Anchorage',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/New_York',
  'America/Phoenix',
  'America/Sao_Paulo',
  'Asia/Bangkok',
  'Asia/Dubai',
  'Asia/Hong_Kong',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Brisbane',
  'Australia/Melbourne',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Dublin',
  'Europe/London',
  'Europe/Madrid',
  'Europe/Paris',
  'Pacific/Auckland',
  'Pacific/Honolulu',
] as const

const TIMEZONE_ALIASES: Record<string, string[]> = {
  UTC: ['utc', 'gmt', 'zulu'],
  'America/Los_Angeles': ['pst', 'pdt', 'pt', 'pacific', 'pacific time', 'los angeles', 'la', 'seattle', 'san francisco', 'us pacific'],
  'America/New_York': ['est', 'edt', 'et', 'eastern', 'eastern time', 'new york', 'nyc', 'us eastern'],
  'America/Chicago': ['cst', 'cdt', 'ct', 'central', 'central time', 'chicago', 'us central'],
  'America/Denver': ['mst', 'mdt', 'mt', 'mountain', 'mountain time', 'denver', 'us mountain'],
  'America/Phoenix': ['mst', 'arizona', 'phoenix'],
  'Europe/London': ['london', 'uk', 'britain'],
} as const

const COMMON_TIMEZONES = new Set<string>([
  'UTC',
  'America/Los_Angeles',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'Europe/London',
])

function sortAndUnique(values: string[]): string[] {
  const set = new Set<string>()
  values.forEach((value) => {
    const tz = String(value || '').trim()
    if (tz) set.add(tz)
  })
  return Array.from(set).sort((a, b) => a.localeCompare(b))
}

export function getTimeZoneIds(): string[] {
  const intlWithSupportedValues = Intl as Intl.DateTimeFormatOptions & {
    supportedValuesOf?: (key: string) => string[]
  }
  const supported = typeof intlWithSupportedValues.supportedValuesOf === 'function'
    ? intlWithSupportedValues.supportedValuesOf('timeZone')
    : []
  const merged = sortAndUnique([...TIMEZONE_FALLBACK, ...(Array.isArray(supported) ? supported : [])])
  return merged.includes('UTC') ? merged : ['UTC', ...merged]
}

export function isValidTimeZoneId(value: string): boolean {
  const tz = String(value || '').trim()
  if (!tz) return false
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date())
    return true
  } catch {
    return false
  }
}

export function getTimeZoneOffsetLabel(timeZone: string, date = new Date()): string {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
    const parts = fmt.formatToParts(date)
    const offset = parts.find((part) => part.type === 'timeZoneName')?.value || ''
    if (!offset || offset === 'GMT') return 'UTC+00:00'
    const normalized = offset.replace('GMT', 'UTC')
    const hasMinutes = /UTC[+-]\d{1,2}:\d{2}$/.test(normalized)
    if (hasMinutes) return normalized
    const match = normalized.match(/^UTC([+-]\d{1,2})$/)
    if (!match) return normalized
    const hour = match[1]
    const sign = hour.startsWith('-') ? '-' : '+'
    const absHour = hour.replace(/[+-]/, '').padStart(2, '0')
    return `UTC${sign}${absHour}:00`
  } catch {
    return ''
  }
}

export function getTimeZoneOptionLabel(timeZone: string): string {
  const offset = getTimeZoneOffsetLabel(timeZone)
  return offset ? `${timeZone} (${offset})` : timeZone
}

function timezoneSearchTerms(timeZone: string): string[] {
  const tz = String(timeZone || '').trim()
  if (!tz) return []
  const lower = tz.toLowerCase()
  const parts = lower.split('/')
  const city = parts[1] ? parts[1].replace(/_/g, ' ') : ''
  const region = parts[0] || ''
  const aliases = TIMEZONE_ALIASES[tz] || []
  return [lower, lower.replace(/_/g, ' '), city, region, ...aliases]
    .map((term) => String(term || '').trim().toLowerCase())
    .filter(Boolean)
}

export function searchTimeZoneIds(allTimeZones: string[], query: string, limit = 40): string[] {
  const q = String(query || '').trim().toLowerCase()
  const rows = Array.isArray(allTimeZones) ? allTimeZones : []
  if (!q) return rows.slice(0, limit)

  const scored = rows
    .map((tz) => {
      const terms = timezoneSearchTerms(tz)
      let score = Number.POSITIVE_INFINITY
      for (const term of terms) {
        if (term === q) score = Math.min(score, 0)
        else if (term.startsWith(q)) score = Math.min(score, 1)
        else if (term.includes(q) || q.includes(term)) score = Math.min(score, 2)
      }
      if (!Number.isFinite(score)) return null
      const commonBoost = COMMON_TIMEZONES.has(tz) ? -1 : 0
      return { tz, score: score + commonBoost }
    })
    .filter((row): row is { tz: string; score: number } => Boolean(row))
    .sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score
      return a.tz.localeCompare(b.tz)
    })

  return scored.slice(0, limit).map((row) => row.tz)
}
